import type { DesignBody, QuoteDesignViewReady } from '@repo/api-contracts';
import { FACE_KEYS } from '@repo/api-contracts';
import { type FaceRenderDeps, viewableFaces } from '@repo/design-viewer';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductionOrderSheetFaceImage } from './production-order-sheet-face-image';

interface ProductionOrderSheetFacesProps {
  readonly view: QuoteDesignViewReady;
  readonly body: DesignBody;
  readonly refreshUrls: () => Promise<void>;
  /** 그릴 면이 모두 끝났을 때(없으면 바로) 한 번. */
  readonly onAllSettled: () => void;
  readonly deps?: FaceRenderDeps;
}

/** 발주서 "디자인 시안" — 디자인이 있는 면마다 브라우저에서 그려 가로로 나열한다. */
export const ProductionOrderSheetFaces = ({
  view,
  body,
  refreshUrls,
  onAllSettled,
  deps,
}: ProductionOrderSheetFacesProps): ReactElement | null => {
  const faceKeys = useMemo(() => {
    const drawable = new Set(
      viewableFaces(view.definition, body)
        .filter((face) => face.hasLayers && face.kind === 'design')
        .map((face) => face.face),
    );
    return FACE_KEYS.filter((key) => drawable.has(key));
  }, [view.definition, body]);
  const assetUrls = useMemo(
    () => new Map(view.assets.map((asset) => [asset.id, asset.url])),
    [view.assets],
  );
  const [settled, setSettled] = useState<ReadonlySet<string>>(() => new Set());
  const notifiedRef = useRef(false);
  const total = faceKeys.length;

  const markSettled = useCallback((key: string): void => {
    setSettled((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);

  useEffect(() => {
    if (notifiedRef.current || settled.size < total) return;
    notifiedRef.current = true;
    onAllSettled();
  }, [settled, total, onAllSettled]);

  if (total === 0) return null;

  return (
    <div className="flex flex-wrap gap-4 print:break-inside-avoid">
      {faceKeys.map((faceKey) => (
        <ProductionOrderSheetFaceImage
          key={faceKey}
          faceKey={faceKey}
          definition={view.definition}
          colorOptionId={view.colorOptionId}
          body={body}
          assetUrls={assetUrls}
          fonts={view.fonts}
          inks={view.inkColors}
          refreshUrls={refreshUrls}
          onSettled={() => markSettled(faceKey)}
          deps={deps}
        />
      ))}
    </div>
  );
};
